import { useMemo } from "react"
import { Button, Card, CardBody, Col, Row, Table, Badge } from "reactstrap"
import { Link } from "react-router-dom"
import { useAccountDashboard } from "hooks/useAccountDashboard"
import { useAccountVendorBills } from "hooks/useAccountVendorBill"
import { useAccountTransactions } from "hooks/useAccountTransaction"
import { useAccountExpenses } from "hooks/useAccountExpense"
import { useAccountFundTransfers } from "hooks/useAccountFundTransfer"
import { useAccountCustomerPayments } from "hooks/useAccountCustomerPayment"
import { useAccountVendorPayments } from "hooks/useAccountVendorPayment"
import { useAccountLoanRepayments } from "hooks/useAccountLoanRepayment"
import { formatCurrency } from "utils/formatCurrency"
import { formatReadableDate } from "utils/formatDate"
import ReversalBadge from "pages/AccountPages/utils/ReversalBadge"
import { getReversedAmount, getReversedCount, getReversedRecords, sumAmounts } from "pages/AccountPages/utils/reversalHelpers"
import WorkspaceStatCard from "../components/WorkspaceStatCard"

const OverviewTab = ({ permissionFlags, onQuickAction, onTabChange }) => {
  const { data: dashboardResponse, isLoading: dashboardLoading } =
    useAccountDashboard(permissionFlags.canViewAnyFinance)
  const { data: billsResponse, isLoading: billsLoading } = useAccountVendorBills(
    { page: 1, per_page: 5 },
    permissionFlags.canViewVendorBills
  )
  const { data: transactionResponse, isLoading: txnLoading } = useAccountTransactions(
    { page: 1, per_page: 6 },
    permissionFlags.canViewTransactions
  )
  const { data: expenseResponse } = useAccountExpenses({ page: 1, per_page: 6 }, permissionFlags.canViewExpenses)
  const { data: depositResponse } = useAccountFundTransfers({ page: 1, per_page: 6 }, permissionFlags.canViewFundTransfers)
  const { data: customerPaymentResponse } = useAccountCustomerPayments({ page: 1, per_page: 6 }, permissionFlags.canViewCustomerPayments)
  const { data: vendorPaymentResponse } = useAccountVendorPayments({ page: 1, per_page: 6 }, permissionFlags.canViewVendorPayments)
  const { data: repaymentResponse } = useAccountLoanRepayments({ page: 1, per_page: 6 }, permissionFlags.canViewLoanRepayments)

  const dashboard = dashboardResponse?.data || {}
  const recentBills = billsResponse?.data?.data || []
  const recentTransactions = transactionResponse?.data?.data || []
  const recentExpenses = expenseResponse?.data?.data || []
  const recentDeposits = depositResponse?.data?.data || []
  const recentCustomerPayments = customerPaymentResponse?.data?.data || []
  const recentVendorPayments = vendorPaymentResponse?.data?.data || []
  const recentRepayments = repaymentResponse?.data?.data || []

  const billAttention = useMemo(
    () =>
      recentBills.filter(item => {
        const slug = item.bill_status?.slug || item.billStatus?.slug || ""
        return slug === "due" || slug === "partial"
      }),
    [recentBills]
  )

  const quickActions = [
    permissionFlags.canAddExpense && { label: "Add Expense", icon: "mdi mdi-receipt-text-plus-outline", action: () => onQuickAction("expense") },
    permissionFlags.canAddVendorBill && { label: "Add Vendor Bill", icon: "mdi mdi-file-plus-outline", action: () => onQuickAction("vendor-bill") },
    permissionFlags.canAddVendorPayment && { label: "Record Payment", icon: "mdi mdi-cash-plus", action: () => onQuickAction("vendor-payment") },
    permissionFlags.canAddLoan && { label: "Issue Loan", icon: "mdi mdi-cash", action: () => onQuickAction("loan") },
    permissionFlags.canAddRepayment && { label: "Add Repayment", icon: "mdi mdi-cash-sync", action: () => onQuickAction("loan-repayment") },
    permissionFlags.canAddFundTransfer && { label: "Add Fund Transfer", icon: "mdi mdi-bank-transfer-in", action: () => onQuickAction("fund-transfer") },
  ].filter(Boolean)

  const adjustmentFeed = useMemo(() => {
    const mapped = [
      ...getReversedRecords(recentExpenses).map(item => ({ id: `expense-${item.id}`, module: "Expense", reference: `Expense #${item.id}`, date: item.expense_date, amount: item.amount, type: "reversed" })),
      ...getReversedRecords(recentDeposits).map(item => ({ id: `deposit-${item.id}`, module: "Fund Transfer", reference: `Transfer #${item.id}`, date: item.transfer_date, amount: item.amount, type: "reversed" })),
      ...getReversedRecords(recentCustomerPayments).map(item => ({ id: `customer-payment-${item.id}`, module: "Customer Payment", reference: `Payment #${item.id}`, date: item.received_date, amount: item.amount, type: "reversed" })),
      ...getReversedRecords(recentVendorPayments).map(item => ({ id: `vendor-payment-${item.id}`, module: "Vendor Payment", reference: `Payment #${item.id}`, date: item.payment_date, amount: item.amount, type: "reversed" })),
      ...getReversedRecords(recentRepayments).map(item => ({ id: `repayment-${item.id}`, module: "Loan Repayment", reference: `Repayment #${item.id}`, date: item.repayment_date, amount: item.amount, type: "reversed" })),
      ...recentTransactions.filter(item => item.is_reversal).map(item => ({ id: `ledger-${item.id}`, module: "Ledger", reference: `${item.reference_type || "entry"} #${item.reference_id || item.id}`, date: item.date, amount: item.amount, type: "reversal_entry" })),
    ]

    return mapped
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
      .slice(0, 8)
  }, [recentExpenses, recentDeposits, recentCustomerPayments, recentVendorPayments, recentRepayments, recentTransactions])

  const adjustmentSummary = useMemo(() => {
    const reversedRecordAmount =
      getReversedAmount(recentExpenses) +
      getReversedAmount(recentDeposits) +
      getReversedAmount(recentCustomerPayments) +
      getReversedAmount(recentVendorPayments) +
      getReversedAmount(recentRepayments)
    const reversalEntryAmount = sumAmounts(recentTransactions.filter(item => item.is_reversal))
    const reversedRecordCount =
      getReversedCount(recentExpenses) +
      getReversedCount(recentDeposits) +
      getReversedCount(recentCustomerPayments) +
      getReversedCount(recentVendorPayments) +
      getReversedCount(recentRepayments)
    const moduleCount = new Set(adjustmentFeed.map(item => item.module)).size

    return {
      adjustedAmount: reversedRecordAmount,
      reversalEntryAmount,
      reversedRecordCount,
      moduleCount,
    }
  }, [recentExpenses, recentDeposits, recentCustomerPayments, recentVendorPayments, recentRepayments, recentTransactions, adjustmentFeed])

  return (
    <div className="d-flex flex-column gap-4">
      <Row className="g-3">
        {permissionFlags.canViewAccounts && (
          <Col md={6} xl={3}>
            <WorkspaceStatCard
              title="Account Balance"
              value={dashboardLoading ? "..." : formatCurrency(dashboard.total_balance || 0, "BDT")}
              subtitle="Total across visible finance accounts"
              icon="mdi mdi-bank"
              color="primary"
            />
          </Col>
        )}
        {permissionFlags.canViewLoans && (
          <Col md={6} xl={3}>
            <WorkspaceStatCard
              title="Outstanding Loans"
              value={dashboardLoading ? "..." : formatCurrency(dashboard.outstanding_loans?.total || 0, "BDT")}
              subtitle={`${dashboard.outstanding_loans?.count || 0} active loan(s)`}
              icon="mdi mdi-cash"
              color="warning"
            />
          </Col>
        )}
        {permissionFlags.canViewVendorBills && (
          <Col md={6} xl={3}>
            <WorkspaceStatCard
              title="Bills Needing Action"
              value={billsLoading ? "..." : billAttention.length}
              subtitle="Due or partial bills from the latest queue"
              icon="mdi mdi-alert-circle-outline"
              color="danger"
            />
          </Col>
        )}
        {permissionFlags.canViewTransactions && (
          <Col md={6} xl={3}>
            <WorkspaceStatCard
              title="Recent Ledger Entries"
              value={txnLoading ? "..." : recentTransactions.length}
              subtitle="Latest posted transactions"
              icon="mdi mdi-book-open-variant"
              color="success"
            />
          </Col>
        )}
      </Row>

      {!!adjustmentFeed.length && (
        <Row className="g-3">
          <Col md={4}>
            <WorkspaceStatCard
              title="Adjusted Amount"
              value={formatCurrency(adjustmentSummary.adjustedAmount, "BDT")}
              subtitle="Reversed business records in the visible feeds"
              icon="mdi mdi-calculator-variant-outline"
              color="info"
            />
          </Col>
          <Col md={4}>
            <WorkspaceStatCard
              title="Reversed Records"
              value={adjustmentSummary.reversedRecordCount}
              subtitle={`${adjustmentSummary.moduleCount} module(s) affected`}
              icon="mdi mdi-undo-variant"
              color="secondary"
            />
          </Col>
          <Col md={4}>
            <WorkspaceStatCard
              title="Reversal Entries"
              value={formatCurrency(adjustmentSummary.reversalEntryAmount, "BDT")}
              subtitle="Counter-entries posted in the recent ledger feed"
              icon="mdi mdi-swap-horizontal"
              color="warning"
            />
          </Col>
        </Row>
      )}

      <Row className="g-3">
        <Col xl={5}>
          <Card className="border-0 shadow-sm h-100">
            <CardBody>
              <h5 className="mb-1">Quick Actions</h5>
              <p className="text-muted mb-3 small">Start the next finance task without leaving the workspace.</p>
              <div className="d-grid gap-2">
                {quickActions.length ? quickActions.map(item => (
                  <Button key={item.label} color="light" className="text-start border" onClick={item.action}>
                    <i className={`${item.icon} me-2 text-primary`} />
                    {item.label}
                  </Button>
                )) : (
                  <div className="text-muted small">No quick actions are available for this role.</div>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl={7}>
          <Card className="border-0 shadow-sm h-100">
            <CardBody>
              <h5 className="mb-1">Needs Attention</h5>
              <p className="text-muted mb-3 small">The most immediate items based on your visible finance modules.</p>
              <div className="d-flex flex-column gap-2">
                {permissionFlags.canViewVendorBills && billAttention.slice(0, 3).map(item => (
                  <div key={item.id} className="border rounded-3 p-3">
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div>
                        <div className="fw-semibold">{item.vendor?.name || "Vendor"}</div>
                        <div className="text-muted small">{item.bill_no || `Bill #${item.id}`}</div>
                        <div className="text-muted small">{item.bill_date ? formatReadableDate(item.bill_date) : "No bill date"}</div>
                      </div>
                      <div className="text-end">
                        <Badge color="danger" className="mb-2">
                          {item.bill_status?.name || item.billStatus?.name || "Attention"}
                        </Badge>
                        <div className="fw-bold">{formatCurrency(item.amount || 0, "BDT")}</div>
                      </div>
                    </div>
                  </div>
                ))}
                {!billAttention.length && permissionFlags.canViewLoans && (
                  <div className="border rounded-3 p-3">
                    <div className="fw-semibold">Loans summary is up to date</div>
                    <div className="text-muted small">Open the Loans tab to manage active balances and repayments.</div>
                    <Button color="link" className="px-0 mt-2" onClick={() => onTabChange("loans")}>Go to Loans</Button>
                  </div>
                )}
                {!billAttention.length && !permissionFlags.canViewLoans && (
                  <div className="text-muted small">No finance items need immediate attention right now.</div>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        {permissionFlags.canViewVendorBills && (
          <Col xl={6}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="mb-1">Recent Vendor Bills</h5>
                    <p className="text-muted mb-0 small">Latest bills so payables work starts from one queue.</p>
                  </div>
                  <Link to="/account-vendor-bill-list" className="btn btn-sm btn-light border">Open full list</Link>
                </div>
                <div className="table-responsive">
                  <Table size="sm" className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Bill</th>
                        <th>Vendor</th>
                        <th>Status</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentBills.length ? recentBills.map(item => (
                        <tr key={item.id}>
                          <td>
                            <div className="fw-semibold">{item.bill_no || `Bill #${item.id}`}</div>
                            <div className="text-muted small">{item.bill_date ? formatReadableDate(item.bill_date) : "-"}</div>
                          </td>
                          <td>{item.vendor?.name || "-"}</td>
                          <td><Badge color="light" className="text-dark">{item.bill_status?.name || item.billStatus?.name || "-"}</Badge></td>
                          <td className="text-end fw-semibold">{formatCurrency(item.amount || 0, "BDT")}</td>
                        </tr>
                      )) : (
                        <tr><td colSpan="4" className="text-center text-muted py-4">No bills found.</td></tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        )}

        {permissionFlags.canViewTransactions && (
          <Col xl={6}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="mb-1">Recent Transactions</h5>
                    <p className="text-muted mb-0 small">Quick visibility into the latest money movement.</p>
                  </div>
                  <Link to="/account-transaction-list" className="btn btn-sm btn-light border">Open ledger</Link>
                </div>
                <div className="table-responsive">
                  <Table size="sm" className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Account</th>
                        <th>Type</th>
                        <th>Date</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentTransactions.length ? recentTransactions.map(item => {
                        const isCredit = (item.transaction_type?.name || item.transactionType?.name || "").toLowerCase().includes("credit")
                        return (
                          <tr key={item.id}>
                            <td>{item.account?.name || "-"}</td>
                            <td>
                              <div className="d-flex gap-1 align-items-center">
                                <Badge color={isCredit ? "success" : "danger"}>{item.transaction_type?.name || item.transactionType?.name || "-"}</Badge>
                                <ReversalBadge isReversal={item.is_reversal} isReversed={item.is_reversed} />
                              </div>
                            </td>
                            <td>{item.date ? formatReadableDate(item.date) : "-"}</td>
                            <td className={`text-end fw-semibold ${isCredit ? "text-success" : "text-danger"}`}>
                              {isCredit ? "+" : "-"}{formatCurrency(item.amount || 0, item.account?.currency || "BDT")}
                            </td>
                          </tr>
                        )
                      }) : (
                        <tr><td colSpan="4" className="text-center text-muted py-4">No transactions found.</td></tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        )}
      </Row>

      {!!adjustmentFeed.length && (
        <Card className="border-0 shadow-sm">
          <CardBody>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h5 className="mb-1">Recent Adjustments</h5>
                <p className="text-muted mb-0 small">Latest reversals and counter-entries across the finance workspace.</p>
              </div>
              <Button color="light" className="border" onClick={() => onTabChange("ledger")}>Open Ledger</Button>
            </div>
            <div className="table-responsive">
              <Table size="sm" className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th>Reference</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {adjustmentFeed.map(item => (
                    <tr key={item.id}>
                      <td className="fw-semibold">{item.module}</td>
                      <td>{item.reference}</td>
                      <td>{item.date ? formatReadableDate(item.date) : "-"}</td>
                      <td>
                        <ReversalBadge isReversal={item.type === "reversal_entry"} isReversed={item.type === "reversed"} />
                      </td>
                      <td className="text-end fw-semibold">{formatCurrency(item.amount || 0, "BDT")}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  )
}

export default OverviewTab
